/*
 * Turns any error thrown by axios into one sentence we can actually show.
 *
 * The API answers in several different shapes depending on which controller
 * replied, so reading `error.response.data.message` on its own prints
 * "undefined" for a large part of the surface:
 *
 *   { "message": "..." }                              abort() and most handlers
 *   { "errors": { "email": ["..."], ... } }           validation (422)
 *   { "errors": { "authentication": ["..."] } }       auth failures
 *   { "error":  ["..."] }                             several controllers
 *   { "success": [...] }                              success, never an error
 *
 * Order matters: the most specific message wins, and we fall back to something
 * meaningful rather than to the word "undefined".
 */

const FALLBACK = "Something went wrong. Please try again.";

//pull the first string out of {field: ["a","b"]} or ["a","b"] or "a"
function firstString(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value.trim() || null;

  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = firstString(entry);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === "object") {
    for (const key of Object.keys(value)) {
      const found = firstString(value[key]);
      if (found) return found;
    }
  }

  return null;
}

//every validation message, so a form can show more than the first problem
export function apiErrorList(error) {
  const data = error && error.response ? error.response.data : null;
  if (!data) return [];

  const messages = [];
  const collect = (value) => {
    if (typeof value === "string") {
      if (value.trim()) messages.push(value.trim());
    } else if (Array.isArray(value)) {
      value.forEach(collect);
    } else if (value && typeof value === "object") {
      Object.keys(value).forEach((key) => collect(value[key]));
    }
  };

  if (data.errors) collect(data.errors);
  else if (data.error) collect(data.error);
  else if (data.message) collect(data.message);

  return messages;
}

//the one line to put in a toast
export function apiErrorMessage(error, fallback = FALLBACK) {
  if (!error) return fallback;

  //the request never made it out: offline, DNS, CORS, cancelled
  if (!error.response) {
    if (error.message && /network/i.test(error.message)) {
      return "Cannot reach the server. Check your connection and try again.";
    }
    return error.message || fallback;
  }

  const { status, data } = error.response;

  //a response fetched with responseType "blob" hands back a Blob, so reading
  //.message off it gives undefined and the real reason never surfaces. The body
  //has to be read asynchronously, so report the status here and let
  //apiErrorFromBlob be used where the detail matters.
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return statusMessage(status) || fallback;
  }

  //Laravel sends a plain string body for some error pages
  if (typeof data === "string" && data.trim() && !data.startsWith("<")) {
    return data.trim();
  }

  if (data && typeof data === "object") {
    //validation and auth errors are the most specific, so they win
    const fromErrors = firstString(data.errors);
    if (fromErrors) return fromErrors;

    const fromError = firstString(data.error);
    if (fromError) return fromError;

    const fromMessage = firstString(data.message);
    if (fromMessage) return fromMessage;
  }

  //nothing usable in the body, so at least explain the status
  return statusMessage(status) || fallback;
}

function statusMessage(status) {
  switch (status) {
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You do not have permission to do that.";
    case 404:
      return "That record no longer exists.";
    case 419:
      return "Your session has expired. Please sign in again.";
    case 422:
      return "Some of the information entered is not valid.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
    case 502:
    case 503:
      return "The server had a problem completing that request.";
    default:
      return null;
  }
}

/**
 * Reads a blob error body and resolves to its message.
 * Use where the request set responseType: "blob".
 */
export function apiErrorFromBlob(error, fallback = FALLBACK) {
  const data = error && error.response ? error.response.data : null;

  if (typeof Blob === "undefined" || !(data instanceof Blob)) {
    return Promise.resolve(apiErrorMessage(error, fallback));
  }

  return data
    .text()
    .then((text) => {
      try {
        const parsed = JSON.parse(text);
        return apiErrorMessage(
          { response: { status: error.response.status, data: parsed } },
          fallback
        );
      } catch (e) {
        return text && text.trim() && !text.trim().startsWith("<")
          ? text.trim()
          : apiErrorMessage(error, fallback);
      }
    })
    .catch(() => apiErrorMessage(error, fallback));
}

//true when the failure means the session is gone, so the caller can sign out
export function isAuthError(error) {
  if (!error || !error.response) return false;
  if ([401, 419].includes(error.response.status)) return true;

  const message = apiErrorMessage(error, "");
  return typeof message === "string" && message.includes("Unauthenticated");
}

export default apiErrorMessage;
